import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import './ProgramOutcomeYearComparison.css';

function DifferenceGroup({ title, tone, items, renderItem }) {
  return (
    <section className={`pc-evidence-group pc-evidence-group--${tone}`} aria-label={title}>
      <div className="pc-evidence-group__heading">
        <h3>{title}</h3>
        <span>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="pc-evidence-group__empty">Kayıt bulunmuyor.</p>
      ) : (
        <ol className="pc-evidence-list">
          {items.map((item) => renderItem(item))}
        </ol>
      )}
    </section>
  );
}

export default function ProgramOutcomeYearComparison({ programId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reasonDrafts, setReasonDrafts] = useState({});
  const [savingReasonNumber, setSavingReasonNumber] = useState('');

  const loadComparison = useCallback(async () => {
    if (!programId || !token) {
      setLoading(false);
      setError('Karşılaştırma için program bilgisi veya yetki doğrulaması bulunamadı.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/programs/${programId}/program-outcomes/year-comparison`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'PÇ karşılaştırması yüklenemedi.');
      setData(payload);
      setReasonDrafts(Object.fromEntries(
        (payload.changed || []).map((item) => [String(item.number), item.reason || '']),
      ));
    } catch (requestError) {
      setData(null);
      setError(requestError.message || 'PÇ karşılaştırması yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [programId, token]);

  const saveReason = async (item) => {
    const number = String(item.number);
    const reason = String(reasonDrafts[number] || '').trim();
    if (!reason) {
      setError(`PÇ ${item.number} için değişiklik gerekçesi girin.`);
      return;
    }
    setSavingReasonNumber(number);
    setError('');
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/programs/${programId}/program-outcomes/year-comparison/reasons`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            source_year: data.source_year.year,
            target_year: data.target_year.year,
            number: item.number,
            reason,
          }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Gerekçe kaydedilemedi.');
      setData((previous) => ({
        ...previous,
        changed: previous.changed.map((outcome) => (
          String(outcome.number) === number
            ? { ...outcome, reason: payload.reason, reason_updated_at: payload.reason_updated_at }
            : outcome
        )),
      }));
    } catch (requestError) {
      setError(requestError.message || 'Gerekçe kaydedilemedi.');
    } finally {
      setSavingReasonNumber('');
    }
  };

  useEffect(() => { loadComparison(); }, [loadComparison]);

  if (loading) return <div className="pc-evidence-state">PÇ değişiklik kanıtı hazırlanıyor…</div>;
  if (error) {
    return (
      <div className="pc-evidence-state pc-evidence-state--error">
        <p>{error}</p>
        <button type="button" onClick={loadComparison}>Yeniden Dene</button>
      </div>
    );
  }
  if (!data) return null;

  const noChanges = data.change_count === 0;
  const reportTime = data.generated_at
    ? new Intl.DateTimeFormat('tr-TR', { dateStyle: 'long', timeStyle: 'short', timeZone: 'Europe/Istanbul' }).format(new Date(data.generated_at))
    : '—';

  return (
    <div className="pc-evidence" aria-live="polite">
      <section className="pc-evidence-hero">
        <div>
          <span className="pc-evidence-eyebrow">DENETİM KANITI · PÇ YIL KARŞILAŞTIRMASI</span>
          <h2>Program Çıktısı Değişiklik Kaydı</h2>
          <p>
            <strong>{data.source_year.label}</strong> ile <strong>{data.target_year.label}</strong> akademik yıllarındaki
            PÇ kayıtları, PÇ numarası üzerinden karşılaştırılmıştır.
          </p>
        </div>
        <div className={`pc-evidence-result${noChanges ? ' pc-evidence-result--clear' : ''}`}>
          <strong>{data.change_count}</strong>
          <span>{noChanges ? 'değişiklik yok' : 'değişiklik tespit edildi'}</span>
        </div>
      </section>

      <section className="pc-evidence-proof" aria-label="Doğrulama bilgileri">
        <div><span>Program</span><strong>{data.program.name}</strong></div>
        <div><span>Karşılaştırma zamanı</span><strong>{reportTime}</strong></div>
      </section>

      <div className="pc-evidence-actions no-print">
        <button type="button" className="pc-evidence-refresh" onClick={loadComparison}>Karşılaştırmayı Yenile</button>
        <button type="button" className="pc-evidence-print" onClick={() => window.print()}>Kanıt Raporunu Yazdır</button>
      </div>

      <div className="pc-evidence-groups">
        <DifferenceGroup
          title="Metni Değişen PÇ’ler"
          tone="changed"
          items={data.changed || []}
          renderItem={(item) => (
            <li key={`changed-${item.number}`}>
              <span className="pc-evidence-number">PÇ {item.number}</span>
              <div className="pc-evidence-diff">
                <p><span>Eski</span>{item.old_name}</p>
                <p><span>Yeni</span>{item.new_name}</p>
              </div>
              <div className="pc-evidence-reason">
                <div className="pc-evidence-reason__editor no-print">
                  <label htmlFor={`reason-${item.number}`}>Değişiklik gerekçesi</label>
                  <textarea
                    id={`reason-${item.number}`}
                    value={reasonDrafts[String(item.number)] || ''}
                    onChange={(event) => setReasonDrafts((previous) => ({
                      ...previous,
                      [String(item.number)]: event.target.value,
                    }))}
                    placeholder="Bu PÇ'nin neden değiştirildiğini yazın."
                    rows="3"
                  />
                  <button
                    type="button"
                    onClick={() => saveReason(item)}
                    disabled={savingReasonNumber === String(item.number)}
                  >
                    {savingReasonNumber === String(item.number) ? 'Kaydediliyor…' : 'Gerekçeyi Kaydet'}
                  </button>
                </div>
                <p className="pc-evidence-reason__print print-only">
                  <strong>Değişiklik gerekçesi:</strong> {item.reason || 'Gerekçe girilmedi.'}
                </p>
              </div>
            </li>
          )}
        />
        <DifferenceGroup
          title="Eklenen PÇ’ler"
          tone="added"
          items={data.added || []}
          renderItem={(item) => (
            <li key={`added-${item.number}`}>
              <span className="pc-evidence-number">PÇ {item.number}</span>
              <p>{item.new_name}</p>
            </li>
          )}
        />
        <DifferenceGroup
          title="Kaldırılan PÇ’ler"
          tone="removed"
          items={data.removed || []}
          renderItem={(item) => (
            <li key={`removed-${item.number}`}>
              <span className="pc-evidence-number">PÇ {item.number}</span>
              <p>{item.old_name}</p>
            </li>
          )}
        />
      </div>

      <p className="pc-evidence-note">
        Değişmeyen PÇ sayısı: <strong>{data.unchanged_count}</strong>. Bu rapor, iki akademik yıla ait ayrı PÇ kayıtlarının
        karşılaştırılmasıyla oluşturulur; eski yıl kaydını değiştirmez.
      </p>
    </div>
  );
}
